import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const RealTimeFeed = ({ title = "Live Social Feed" }) => {
  const [posts, setPosts] = useState([]);
  const [isConnected, setIsConnected] = useState(true);

  useEffect(() => {
    // Mock real-time posts data
    const mockPosts = [
      {
        id: 1,
        platform: 'instagram',
        author: '@dru_travels',
        content: `Just booked an amazing trip to Ooty through Dru Travels! The hill station package looks incredible. Can't wait to explore the tea gardens! 🌿 #OotyTrip #DruTravels`,timestamp: new Date(Date.now() - 300000),likes: 45,comments: 12,shares: 8,sentiment: 'positive',avatar: 'https://images.unsplash.com/photo-1494790108755-2616b9e0e4d4?w=40&h=40&fit=crop&crop=face'
      },
      {
        id: 2,
        platform: 'twitter',author: '@travel_enthusiast',
        content: `@dru_travels provided excellent service for our Kerala backwaters tour. Highly recommend their packages for authentic South Indian experiences! 🛶`,
        timestamp: new Date(Date.now() - 600000),
        likes: 23,
        comments: 5,
        shares: 15,
        sentiment: 'positive',avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=40&h=40&fit=crop&crop=face'
      },
      {
        id: 3,
        platform: 'instagram',author: '@wanderlust_india',
        content: `The Mahabalipuram heritage tour was educational and well-organized. Thanks @dru_travels for the knowledgeable guide! 🏛️ #Heritage #TamilNadu`,
        timestamp: new Date(Date.now() - 900000),
        likes: 67,
        comments: 18,
        shares: 22,
        sentiment: 'positive',avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=40&h=40&fit=crop&crop=face'
      },
      {
        id: 4,
        platform: 'twitter',author: '@family_trips',content: `Had some issues with the initial booking process, but @dru_travels customer support resolved everything quickly. Great recovery! 👍`,timestamp: new Date(Date.now() - 1200000),likes: 12,comments: 3,shares: 4,sentiment: 'neutral',avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=40&h=40&fit=crop&crop=face'
      },
      {
        id: 5,
        platform: 'instagram',author: '@solo_traveler_in',content: `The Kodaikanal solo travel package was perfect! Safe, well-planned, and met other amazing travelers. @dru_travels knows solo travel! 🏔️`,timestamp: new Date(Date.now() - 1800000),likes: 89,comments: 25,shares: 31,sentiment: 'positive',avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=40&h=40&fit=crop&crop=face'
      }
    ];

    setPosts(mockPosts);

    // Simulate real-time updates
    const interval = setInterval(() => {
      const newPost = {
        id: Date.now(),
        platform: Math.random() > 0.5 ? 'instagram' : 'twitter',
        author: '@new_traveler',
        content: `Just discovered @dru_travels! Their Tamil Nadu packages look amazing. Planning my next adventure! ✈️`,
        timestamp: new Date(),
        likes: Math.floor(Math.random() * 50),
        comments: Math.floor(Math.random() * 20),
        shares: Math.floor(Math.random() * 15),
        sentiment: 'positive',
        avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=40&h=40&fit=crop&crop=face'
      };

      setPosts(prev => [newPost, ...prev.slice(0, 4)]);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const getPlatformIcon = (platform) => {
    return platform === 'instagram' ? 'Instagram' : 'Twitter';
  };

  const getPlatformColor = (platform) => {
    return platform === 'instagram' ? 'text-pink-500' : 'text-blue-500';
  };

  const getSentimentColor = (sentiment) => {
    if (sentiment === 'positive') return 'text-success';
    if (sentiment === 'negative') return 'text-error';
    return 'text-warning';
  };

  const getSentimentIcon = (sentiment) => {
    if (sentiment === 'positive') return 'ThumbsUp';
    if (sentiment === 'negative') return 'ThumbsDown';
    return 'Minus';
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return timestamp.toLocaleDateString();
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        <div className="flex items-center space-x-2">
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-success' : 'bg-error'} animate-pulse`} />
          <span className="text-sm text-muted-foreground">
            {isConnected ? 'Live' : 'Disconnected'}
          </span>
        </div>
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {posts.map((post) => (
          <div key={post.id} className="border border-border rounded-lg p-4 hover:bg-muted/50 transition-micro">
            <div className="flex items-start space-x-3">
              <Image
                src={post.avatar}
                alt={post.author}
                className="w-10 h-10 rounded-full object-cover"
              />
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-2">
                  <span className="font-medium text-foreground text-sm">{post.author}</span>
                  <Icon 
                    name={getPlatformIcon(post.platform)} 
                    size={16} 
                    className={getPlatformColor(post.platform)}
                  />
                  <span className="text-xs text-muted-foreground">
                    {formatTimestamp(post.timestamp)}
                  </span>
                  <Icon 
                    name={getSentimentIcon(post.sentiment)} 
                    size={14} 
                    className={getSentimentColor(post.sentiment)}
                  />
                </div>
                
                <p className="text-sm text-foreground mb-3 leading-relaxed">
                  {post.content}
                </p>
                
                <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                  <div className="flex items-center space-x-1">
                    <Icon name="Heart" size={14} />
                    <span>{post.likes}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="MessageCircle" size={14} />
                    <span>{post.comments}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Share" size={14} />
                    <span>{post.shares}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RealTimeFeed;